import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import { useGtmPageview } from './hooks/useGtmPageview';
import { Toaster } from '@/components/ui/toaster';
import { AuthModalProvider } from '@/contexts/AuthModalContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { UpgradeModalProvider } from '@/contexts/UpgradeModalContext';
import { StateYearProvider } from '@/contexts/StateYearContext';
import { AppLayout, ScrollToTop } from '@/components/layout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { PageLoadingSpinner } from '@/components/ui/PageLoadingSpinner';
import {
  IndexPageSkeleton,
  BillsPageSkeleton,
  BillDetailsPageSkeleton,
  LegislatorsPageSkeleton,
  LegislatorDetailPageSkeleton,
  CommitteesPageSkeleton,
  CommitteeDetailPageSkeleton,
  CalendarPageSkeleton,
  DashboardPageSkeleton,
  SearchPageSkeleton,
  AccountPageSkeleton,
  ContentPageSkeleton,
  TestimonyPageSkeleton,
  VoteComparisonPageSkeleton,
  VoteDetailsPageSkeleton,
  FindYourLegislatorPageSkeleton,
} from '@/components/ui/skeletons';

// Lazy load onboarding components (uses driver.js)
const OnboardingChecklist = lazy(() =>
  import('@/components/onboarding').then((m) => ({ default: m.OnboardingChecklist }))
);
const TourRunner = lazy(() =>
  import('@/components/onboarding').then((m) => ({ default: m.TourRunner }))
);
const OnboardingTriggers = lazy(() =>
  import('@/components/onboarding').then((m) => ({ default: m.OnboardingTriggers }))
);

// Lazy load all page components
const Index = lazy(() => import('@/pages/Index').then((m) => ({ default: m.Index })));
const Account = lazy(() => import('@/pages/Account').then((m) => ({ default: m.Account })));
const Bills = lazy(() => import('@/pages/Bills').then((m) => ({ default: m.Bills })));
const Legislators = lazy(() =>
  import('@/pages/Legislators').then((m) => ({ default: m.Legislators }))
);
const LegislatorVotes = lazy(() =>
  import('@/pages/LegislatorVotes').then((m) => ({ default: m.LegislatorVotes }))
);
const Committees = lazy(() =>
  import('@/pages/Committees').then((m) => ({ default: m.Committees }))
);
const CommitteeDetail = lazy(() =>
  import('@/pages/CommitteeDetail').then((m) => ({ default: m.CommitteeDetail }))
);
const Calendar = lazy(() => import('@/pages/Calendar').then((m) => ({ default: m.Calendar })));
const Testimony = lazy(() => import('@/pages/Testimony').then((m) => ({ default: m.Testimony })));
const SearchPage = lazy(() =>
  import('@/pages/SearchPage').then((m) => ({ default: m.SearchPage }))
);
const DataSources = lazy(() =>
  import('@/pages/DataSources').then((m) => ({ default: m.DataSources }))
);
const Privacy = lazy(() => import('@/pages/Privacy').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('@/pages/Terms').then((m) => ({ default: m.Terms })));
const About = lazy(() => import('@/pages/About').then((m) => ({ default: m.About })));
const Mission = lazy(() => import('@/pages/Mission').then((m) => ({ default: m.Mission })));
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const NotFound = lazy(() => import('@/pages/NotFound').then((m) => ({ default: m.NotFound })));

// Lazy load heavy detail pages
const BillDetails = lazy(() =>
  import('@/pages/BillDetails').then((m) => ({ default: m.BillDetails }))
);
const LegislatorDetail = lazy(() =>
  import('@/pages/LegislatorDetail').then((m) => ({ default: m.LegislatorDetail }))
);
const VoteDetails = lazy(() =>
  import('@/pages/VoteDetails').then((m) => ({ default: m.VoteDetails }))
);
const VoteComparison = lazy(() =>
  import('@/pages/VoteComparison').then((m) => ({ default: m.VoteComparison }))
);

// Lazy load tools pages
const FindYourLegislator = lazy(() => import('@/pages/tools/FindYourLegislator'));
const FederalLegislatorDetail = lazy(() =>
  import('@/pages/FederalLegislatorDetail').then((m) => ({ default: m.FederalLegislatorDetail }))
);

// Lazy load legacy redirect components
const BillRedirect = lazy(() =>
  import('@/components/routing/LegacyRedirects').then((m) => ({ default: m.BillRedirect }))
);
const LegislatorRedirect = lazy(() =>
  import('@/components/routing/LegacyRedirects').then((m) => ({
    default: m.LegislatorRedirect,
  }))
);
const LegislatorVotesRedirect = lazy(() =>
  import('@/components/routing/LegacyRedirects').then((m) => ({
    default: m.LegislatorVotesRedirect,
  }))
);
const CommitteeRedirect = lazy(() =>
  import('@/components/routing/LegacyRedirects').then((m) => ({
    default: m.CommitteeRedirect,
  }))
);
const VoteRedirect = lazy(() =>
  import('@/components/routing/LegacyRedirects').then((m) => ({ default: m.VoteRedirect }))
);

// Lazy load OAuth pages
const OAuthAuthorize = lazy(() =>
  import('@/pages/oauth/AuthorizePage').then((m) => ({ default: m.AuthorizePage }))
);

// Lazy load admin routes (heavy: recharts, admin-only)
const Pipeline = lazy(() =>
  import('@/pages/admin/Pipeline').then((m) => ({ default: m.Pipeline }))
);
const Schedules = lazy(() =>
  import('@/pages/admin/Schedules').then((m) => ({ default: m.Schedules }))
);
const BillStatus = lazy(() =>
  import('@/pages/admin/BillStatus').then((m) => ({ default: m.BillStatus }))
);
const Notifications = lazy(() =>
  import('@/pages/admin/Notifications').then((m) => ({ default: m.Notifications }))
);
const Reports = lazy(() => import('@/pages/admin/Reports').then((m) => ({ default: m.Reports })));
const CommitteeReports = lazy(() =>
  import('@/pages/admin/CommitteeReports').then((m) => ({ default: m.CommitteeReports }))
);
const VoteReports = lazy(() =>
  import('@/pages/admin/VoteReports').then((m) => ({ default: m.VoteReports }))
);
const TopekaBuzz = lazy(() =>
  import('@/pages/admin/TopekaBuzz').then((m) => ({ default: m.TopekaBuzz }))
);
const CalendarsReport = lazy(() =>
  import('@/pages/admin/CalendarsReport').then((m) => ({ default: m.CalendarsReport }))
);
const FederalLegislatorImport = lazy(() =>
  import('@/pages/admin/FederalLegislatorImport').then((m) => ({
    default: m.FederalLegislatorImport,
  }))
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ThemeProvider defaultTheme="system">
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <AuthProvider>
              <AuthModalProvider>
                <UpgradeModalProvider>
                  <GtmPageviewWrapper>
                    <ScrollToTop />
                    <Toaster />
                    <ErrorBoundary>
                      <StateYearProvider>
                        <OnboardingProvider>
                          <Suspense fallback={null}>
                            <TourRunner />
                            <OnboardingChecklist />
                            <OnboardingTriggers />
                          </Suspense>
                          <Routes>
                            {/* Routes with AppLayout (persistent header/footer) */}
                            <Route path="/" element={<AppLayout />}>
                              <Route
                                index
                                element={
                                  <Suspense fallback={<IndexPageSkeleton />}>
                                    <Index />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="search"
                                element={
                                  <Suspense fallback={<SearchPageSkeleton />}>
                                    <SearchPage />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="account"
                                element={
                                  <Suspense fallback={<AccountPageSkeleton />}>
                                    <Account />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="bills"
                                element={
                                  <Suspense fallback={<BillsPageSkeleton />}>
                                    <Bills />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="bills/:stateCode/:sessionCode/:billNumber"
                                element={
                                  <Suspense fallback={<BillDetailsPageSkeleton />}>
                                    <BillDetails />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="bills/:billNumber"
                                element={
                                  <Suspense fallback={<BillDetailsPageSkeleton />}>
                                    <BillRedirect />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="dashboard"
                                element={
                                  <Suspense fallback={<DashboardPageSkeleton />}>
                                    <Dashboard />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="my-bills"
                                element={<Navigate to="/dashboard" replace />}
                              />
                              <Route
                                path="legislators"
                                element={
                                  <Suspense fallback={<LegislatorsPageSkeleton />}>
                                    <Legislators />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="legislators/:stateCode/:sessionCode/:slug"
                                element={
                                  <Suspense fallback={<LegislatorDetailPageSkeleton />}>
                                    <LegislatorDetail />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="legislators/:stateCode/:sessionCode/:slug/votes"
                                element={
                                  <Suspense fallback={<LegislatorDetailPageSkeleton />}>
                                    <LegislatorVotes />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="legislators/:id"
                                element={
                                  <Suspense fallback={<LegislatorDetailPageSkeleton />}>
                                    <LegislatorRedirect />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="legislators/:id/votes"
                                element={
                                  <Suspense fallback={<LegislatorDetailPageSkeleton />}>
                                    <LegislatorVotesRedirect />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="legislator/federal/:bioguideId"
                                element={
                                  <Suspense fallback={<LegislatorDetailPageSkeleton />}>
                                    <FederalLegislatorDetail />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="committees"
                                element={
                                  <Suspense fallback={<CommitteesPageSkeleton />}>
                                    <Committees />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="committees/:stateCode/:sessionCode/:committeeSlug"
                                element={
                                  <Suspense fallback={<CommitteeDetailPageSkeleton />}>
                                    <CommitteeDetail />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="committees/:id"
                                element={
                                  <Suspense fallback={<CommitteeDetailPageSkeleton />}>
                                    <CommitteeRedirect />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="calendar"
                                element={
                                  <Suspense fallback={<CalendarPageSkeleton />}>
                                    <Calendar />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="testimony"
                                element={
                                  <Suspense fallback={<TestimonyPageSkeleton />}>
                                    <Testimony />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="tools/find-your-legislator"
                                element={
                                  <Suspense fallback={<FindYourLegislatorPageSkeleton />}>
                                    <FindYourLegislator />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="votes/:stateCode/:sessionCode/:billNumber/:voteId"
                                element={
                                  <Suspense fallback={<VoteDetailsPageSkeleton />}>
                                    <VoteDetails />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="vote/:voteId"
                                element={
                                  <Suspense fallback={<VoteDetailsPageSkeleton />}>
                                    <VoteRedirect />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="vote-comparison"
                                element={
                                  <Suspense fallback={<VoteComparisonPageSkeleton />}>
                                    <VoteComparison />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="data-sources"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <DataSources />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="privacy"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <Privacy />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="terms"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <Terms />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="about"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <About />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="mission"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <Mission />
                                  </Suspense>
                                }
                              />
                              {/* OAuth routes */}
                              <Route
                                path="oauth/authorize"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <OAuthAuthorize />
                                  </Suspense>
                                }
                              />
                              {/* Admin routes - lazy loaded */}
                              <Route
                                path="admin/pipeline"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <Pipeline />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/schedules"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <Schedules />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/bill-status"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <BillStatus />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/notifications"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <Notifications />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/reports"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <Reports />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/committee-reports"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <CommitteeReports />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/vote-reports"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <VoteReports />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/topeka-buzz"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <TopekaBuzz />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/calendars-report"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <CalendarsReport />
                                  </Suspense>
                                }
                              />
                              <Route
                                path="admin/data/legislator-import"
                                element={
                                  <Suspense fallback={<PageLoadingSpinner />}>
                                    <FederalLegislatorImport />
                                  </Suspense>
                                }
                              />
                              {/* Catch-all 404 - must stay last */}
                              <Route
                                path="*"
                                element={
                                  <Suspense fallback={<ContentPageSkeleton />}>
                                    <NotFound />
                                  </Suspense>
                                }
                              />
                            </Route>
                          </Routes>
                        </OnboardingProvider>
                      </StateYearProvider>
                    </ErrorBoundary>
                  </GtmPageviewWrapper>
                </UpgradeModalProvider>
              </AuthModalProvider>
            </AuthProvider>
          </Router>
        </QueryClientProvider>
      </HelmetProvider>
    </ThemeProvider>
  );
}

function GtmPageviewWrapper({ children }: { children: React.ReactNode }) {
  // Now this is safely inside the Router context, so useLocation() is valid.
  useGtmPageview();
  return <>{children}</>;
}

export default App;
